import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { BadgeSizeType, BadgeType } from '@contracts';

interface BadgeProps {
  type?: string;
  text?: string | null;
  background?: string;
}

/**
 * Badge Component
 *
 * This component is used to display a badge with customizable text, type, and background color.
 * It utilizes styled-components for styling and supports multiple badge types and sizes.
 *
 * @param {string} [type='new'] - Specifies the type of the badge, which can affect its styling. Multiple types can be provided separated by spaces.
 * @param {string | null} [text=''] - The text to display inside the badge. If not provided, it defaults to a translated 'new' string.
 * @param {string} [background=Colors.lilac] - The background color of the badge. Defaults to a lilac color.
 *
 * @returns {React.FC<BadgeProps>} A React functional component that renders a styled badge.
 *
 * Example usage:
 *
 * <Badge type="success" text="Success" background="#28a745" />
 * <Badge type="warning small" text="Warning" />
 * <Badge type="important" />
 */
const Badge: React.FC<BadgeProps> = ({ type = 'new', text = '', background = Colors.lilac }) => {
  const { t } = useTranslation();
  const splitTypes = type.split(' ') as Array<BadgeType | BadgeSizeType>;

  return (
    <StyledBadge $background={background} className={`badge badge-${splitTypes.join(' badge-')}`}>
      {text || t('components.new')}
    </StyledBadge>
  );
};

const StyledBadge = styled.span<{ $background?: string }>`
  overflow: hidden;
  text-align: center;
  font-family: BrownStd;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 13px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 4px 8px;
  color: ${Colors.cream};
  background: ${({ $background }) => $background || Colors.lilac};
  border-radius: 2px;
  white-space: nowrap;

  &.badge-extra-small {
    display: flex;
    padding: 0.25px 3.75px;
    border-radius: 1.5px;
    font-size: 7.5px;
  }

  &.badge-small {
    font-size: 10px;
    line-height: 100%;
    letter-spacing: 0.5px;
    padding: 4px;

    &.badge-plus {
      font-size: 12px;
      font-style: normal;
      font-weight: 700;

      @media (max-width: 768px) {
        font-size: 10px;
      }
    }
  }

  &.badge-large {
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    padding: 6px 6px 4px;
  }

  &.badge-success {
    color: ${Colors.forestGreen};
    background: ${Colors.sage};
  }

  &.badge-warning {
    color: ${Colors.cream};
    background: ${Colors.gold};
  }

  &.badge-positive {
    color: ${Colors.navyBlue};
    background: ${Colors.sage75};
  }

  &.badge-neutral {
    color: ${Colors.navyBlue};
    background: ${Colors.paleGold75};
  }

  &.badge-inconclusive {
    color: ${Colors.navyBlue};
    background: ${Colors.sky};
  }

  &.badge-important {
    color: ${Colors.navyBlue};
    background: ${Colors.paleGold75};
  }

  &.badge-recommended {
    color: ${Colors.navyBlue};
    background: ${Colors.paleGold};
  }

  &.badge-negative {
    color: ${Colors.navyBlue};
    background: ${Colors.carnationPink75};
  }

  &.badge-conclusive {
    color: ${Colors.cream};
    background: ${Colors.navyBlue};
  }

  &.badge-inline-block {
    vertical-align: inline-block;
  }
`;

export default Badge;
